import { ref } from 'vue'

export interface ConfirmationModalConfig {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'error' | 'warning' | 'info'
  onConfirm: () => Promise<void> | void
  onCancel?: () => void
}

/**
 * Composable for managing confirmation modals with consistent UX patterns
 *
 * @returns Object with modal state and control methods
 */
export function useConfirmationModal() {
  const isVisible = ref(false)
  const isLoading = ref(false)
  const config = ref<ConfirmationModalConfig | null>(null)

  /**
   * Show confirmation modal with the provided configuration
   */
  const showConfirmation = (modalConfig: ConfirmationModalConfig) => {
    config.value = modalConfig
    isVisible.value = true
  }

  /**
   * Handle confirmation action with loading state
   */
  const handleConfirm = async () => {
    if (!config.value) return

    isLoading.value = true
    try {
      const result = config.value.onConfirm()
      if (result instanceof Promise) {
        await result
      }
      isVisible.value = false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Handle cancel action
   */
  const handleCancel = () => {
    if (config.value?.onCancel) {
      config.value.onCancel()
    }
    isVisible.value = false
    isLoading.value = false
    config.value = null
  }

  return {
    isVisible,
    isLoading,
    config,
    showConfirmation,
    handleConfirm,
    handleCancel
  }
}
